import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react'

// components
import '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Store/ModelViewer.ts'
import ZoomableImage from './ZoomableImage.tsx'
import Rating from './Rating.tsx'
import AddToCartButton from './AddToCartButton.tsx'
import AssetsModal from './AssetsModal.tsx'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import Carousel from '@aytm/app/swidget/base/questions/Ecommerce/components/Carousel.tsx'
import AssetsGate from '@aytm/app/swidget/base/questions/Ecommerce/components/AssetsGate.tsx'
import Placeholder from './Placeholder.tsx'
import { Dropdown } from 'primereact/dropdown'

// functions
import { assetDialogEvent } from '@aytm/app/swidget/base/questions/Ecommerce/utils/assetDialogEvent.ts'
import { formatPriceWithLocale } from '@aytm/app/swidget/base/questions/Ecommerce/utils/formatPriceWithLocale.ts'
import { createNumbersArray } from '@aytm/app/swidget/base/questions/Ecommerce/utils/createNumbersArray.ts'
import { getAssetType } from '@aytm/app/swidget/base/questions/Ecommerce/utils/getAssetType.ts'
import { unescapeTags } from '@aytm/lib/common/func.js'
import { getBigAssetPath, getMediumAssetPath, getSmallAssetPath } from '@aytm/lib/common/assets.ts'
import { hapticEffects } from '@aytm/app/swidget/base/lib/hapticEffects.ts'

// types
import {
  IEcommerceAnswerSchema,
  IEcommerceQuestionSchema,
  assetsCollectionElement,
  IFileAsset,
  IEcommerceVideoAsset,
  ASSETS_TYPES
} from '@aytm/app/swidget/base/questions/Ecommerce/types'
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types'

// custom hooks
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale.ts'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline.ts'
import useAssetsCollectionBuilder from '../../hooks/useAssetsCollectionBuilder.ts'

// styles
import style from './Product.module.scss'
import Subproducts from './Subproducts.tsx'

interface IProductProps {
  answer: IEcommerceAnswerSchema
  schema: IEcommerceQuestionSchema
  quantity: number
  index: number
  deliveryDate: string
  findability: boolean
  onAddToCart: (qnt: number, index: number, dropdown: boolean) => void
  onItemClose: () => void
  onItemSelect: (index: number) => void
  onBuyNow: (qnt: number, index: number) => void
  scrollTop: () => void
}

const Product = ({
  answer,
  schema,
  quantity,
  index,
  deliveryDate,
  findability,
  onAddToCart,
  onItemClose,
  onItemSelect,
  onBuyNow,
  scrollTop
}: IProductProps) => {
  // custom hooks calls
  const viewport = useViewport()
  const timeline = useTimeline()
  const { t, languageTag } = useSurveyLocale()

  // refs
  const productRef = useRef<HTMLDivElement>(null)

  // memoized
  const orderedAssetsCollection = useAssetsCollectionBuilder(answer)
  const assetsCollection: assetsCollectionElement[] = useMemo(
    () =>
      // Move default asset on begining
      orderedAssetsCollection.reduce((acc: assetsCollectionElement[], asset) => {
        if (('default' in asset && asset.default) || ('product' in asset && asset.product.default)) {
          acc.unshift(asset)
        } else {
          acc.push(asset)
        }
        return acc
      }, []),
    [orderedAssetsCollection]
  )
  const smallImageCollection: assetsCollectionElement[] = useMemo(() => assetsCollection.slice(1), [assetsCollection])
  // The page waits for the assets it draws first. A desktop shows the main asset and up to four more, each
  // photo at full size; a phone shows the carousel, its photos at medium size. A model loads in its viewer.
  const pageImages = (viewport.mobile ? assetsCollection : assetsCollection.slice(0, 5)).map(asset => {
    const type = getAssetType(asset)
    if (ASSETS_TYPES.IMAGE === type)
      return viewport.mobile ? getMediumAssetPath(asset as IImageAsset) : getBigAssetPath(asset as IImageAsset)
    if (ASSETS_TYPES.VIDEO === type) return (asset as IEcommerceVideoAsset).video.thumbnail
    return null
  })

  // state
  const [showImages, setShowImages] = useState<boolean>(false)
  const [dropdownValue, setDropdownValue] = useState<string>('1')
  const [openDialog, setOpenDialog] = useState<{ open: boolean; index: number }>({ open: false, index: null })
  const [showProductRow, setShowProductRow] = useState<boolean>(false)
  const isDiscounted = answer.discounted_price > 0 && answer.price > answer.discounted_price

  const onSubitemSelect = (index: number) => {
    onItemClose()
    onItemSelect(index)
  }

  useEffect(() => {
    scrollTop()
  }, [scrollTop])

  useEffect(() => {
    setDropdownValue('1')
  }, [quantity])

  useEffect(() => {
    const productElement = productRef.current
    if (!productElement || viewport.mobile) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowProductRow(!entry.isIntersecting)
      },
      {
        threshold: 0.1
      }
    )
    observer.observe(productElement)
    return () => {
      observer.unobserve(productElement)
    }
  }, [viewport])

  //functions
  const onAssetModalOpen = (index: number) => {
    setOpenDialog({ open: true, index: index })
    timeline.log({
      ref: answer.id,
      type: assetDialogEvent('open', assetsCollection[index])
    })
  }

  // The modal logs the typed close itself — it owns the on-screen asset the close is typed by.
  const onAssetsModalClose = useCallback(() => {
    setOpenDialog({ open: false, index: null })
  }, [])

  // render functions
  const renderSmallAsset = (asset: assetsCollectionElement, index: number) => (
    <>
      {ASSETS_TYPES.VIDEO !== getAssetType(asset) && (
        <>
          <button className={style.expandWrapper} onClick={() => onAssetModalOpen(index + 1)}>
            <div className={style.expand} />
          </button>
          <div className={style.heartWrapper}>
            <EmptyLink color="#CC0000">
              <div className={style.heart} />
            </EmptyLink>
          </div>
        </>
      )}
      {ASSETS_TYPES.IMAGE === getAssetType(asset) && (
        <ZoomableImage
          asset={asset as IImageAsset}
          appearence="small"
          answer={answer}
          assetIndex={orderedAssetsCollection.indexOf(asset)}
        />
      )}
      {ASSETS_TYPES.MODEL === getAssetType(asset) && (
        <div className={style.modelView}>
          <model-viewer
            src={(asset as IFileAsset).file_url}
            camera-controls={false}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      )}
      {ASSETS_TYPES.VIDEO === getAssetType(asset) && (
        <button
          onClick={() => onAssetModalOpen(index + 1)}
          className={style.videoView}
          style={{ backgroundImage: `url(${(asset as IEcommerceVideoAsset).video.thumbnail})` }}
        >
          <div className={style.playIcon} />
        </button>
      )}
    </>
  )

  return (
    <>
      {showProductRow && !viewport.mobile && (
        <div className={style.productRow}>
          <div className={style.productRowInner}>
            <div className={style.infoColumn}>
              {answer.image ? (
                <div
                  className={style.image}
                  role="img"
                  aria-label={answer.image?.alt_text || t('questions', 'img', 'noAlt')}
                  style={{ backgroundImage: `url(${getSmallAssetPath(answer.image)})` }}
                />
              ) : (
                <div className={style.noImage} />
              )}
              <div className={style.info}>
                <EmptyLink color="#CC0000">
                  <div className={style.visit} dangerouslySetInnerHTML={{ __html: answer.sub_title || '' }} />
                </EmptyLink>
                <div className={style.caption} dangerouslySetInnerHTML={{ __html: answer.text || '' }} />
                <div className={style.ratingWrapper}>
                  <Rating rating={answer.rating} size="big" />
                  <div className={style.reviews} dangerouslySetInnerHTML={{ __html: `${answer.reviews}` }} />
                  <EmptyLink color="#CC0000">
                    <i className="pi pi-chevron-down" />
                  </EmptyLink>
                </div>
              </div>
            </div>
            <div className={style.buyColumn}>
              <div className={style.prices}>
                {isDiscounted && (
                  <>
                    <div className={style.discount}>
                      <span
                        className={style.nowPrice}
                        dangerouslySetInnerHTML={{
                          __html: `${formatPriceWithLocale(answer.discounted_price, schema.price_mask, languageTag, true, false)}`
                        }}
                      />
                      <span className={style.reg}>{t('questions', 'ec', 'target', 'was')}</span>
                      {answer.price > 0 && (
                        <span
                          className={`${style.lastPrice}`}
                          dangerouslySetInnerHTML={{
                            __html: `${formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)}`
                          }}
                        />
                      )}
                      {answer.per_count_price && <span className={style.perCount}>{answer.per_count_price}</span>}
                    </div>
                    <div className={style.saving}>
                      <span className={style.sale}>{t('questions', 'ec', 'target', 'newLowerPrice')}</span>
                      <span
                        className={style.save}
                        dangerouslySetInnerHTML={{
                          __html: `${t('questions', 'ec', 'target', 'save')} ${formatPriceWithLocale(
                            answer.price - answer.discounted_price,
                            schema.price_mask,
                            languageTag,
                            true,
                            false
                          )} ${t<(percentage: string) => string>(
                            'questions',
                            'ec',
                            'target',
                            'off'
                          )((((answer.price - answer.discounted_price) / answer.price) * 100).toFixed(1))}`
                        }}
                      ></span>
                    </div>
                  </>
                )}
                {!isDiscounted && answer.price && (
                  <>
                    <span
                      className={style.defaultPrice}
                      dangerouslySetInnerHTML={{
                        __html: formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)
                      }}
                    />
                    {answer.per_count_price && <span className={style.perCount}>{answer.per_count_price}</span>}
                  </>
                )}
              </div>
              <div className={style.buy}>
                {quantity > 0 && !findability ? (
                  <AddToCartButton
                    index={index}
                    value={quantity}
                    findability={findability}
                    width={viewport.mobile ? '100%' : '150px'}
                    size="big"
                    showDropdown={true}
                    limit={schema.max_qty_per_product}
                    onAddToCart={onAddToCart}
                  />
                ) : (
                  <div id={style.topRowProductDropdownContainer}>
                    {viewport.mobile ? (
                      <button
                        disabled={findability && 1 === quantity}
                        className={style.buyNow}
                        onClick={() => onBuyNow(1, index)}
                      >
                        {t('questions', 'ec', 'target', 'buyNow')}
                      </button>
                    ) : (
                      <>
                        {!findability && (
                          <Dropdown
                            fixedDropdownDir="bottom"
                            value={`${dropdownValue}`}
                            options={createNumbersArray(schema.max_qty_per_product || 10, false)}
                            optionLabel="value"
                            valueTemplate={(option: { value: string }) => (
                              <div className={style.dropdownLabel}>
                                {t('questions', 'ec', 'target', 'qty')}&nbsp;<b>{`${option?.value}`}</b>
                              </div>
                            )}
                            onChange={event => setDropdownValue(String(event.value))}
                          />
                        )}
                      </>
                    )}
                    <button
                      disabled={1 === quantity}
                      className={style.add}
                      onClick={() => onAddToCart(Number(dropdownValue), index, true)}
                    >
                      {t('questions', 'ec', 'target', 'addToCart')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {openDialog.open && (
        <AssetsModal
          answer={answer}
          orderedAssetsCollection={orderedAssetsCollection}
          assetsCollection={assetsCollection}
          index={openDialog.index}
          onAssetsModalClose={onAssetsModalClose}
        />
      )}
      <div className={`${style.productWrapper} ${viewport.mobile ? style.productWrapperMobile : ''}`}>
        {!viewport.mobile && (
          <button className={style.back} onClick={onItemClose}>
            {t('questions', 'ec', 'target', 'back')}
          </button>
        )}
        {/* The gate sits inside the product block: its ref must stay mounted for the sticky product row. */}
        <div className={style.product} ref={productRef}>
          <AssetsGate urls={pageImages} loader={<Placeholder className={style.productPlaceholder} />}>
            {viewport.mobile ? (
              <>
                <div className={style.caption} dangerouslySetInnerHTML={{ __html: answer.text }} />
                <div className={style.rating}>
                  <Rating rating={answer.rating} size="big" />
                  <span className={style.reviews} dangerouslySetInnerHTML={{ __html: `${answer.reviews}` }} />
                  {answer.red_label && (
                    <div className={style.newAt}>
                      <span className={style.text}>&nbsp;| {answer.red_label}&nbsp;</span>
                      <span className={style.logo}>¬</span>
                    </div>
                  )}
                </div>
                {answer.best_seller && (
                  <div className={style.highlyRated}>&#9733;&nbsp;{t('questions', 'ec', 'target', 'highly')}</div>
                )}
                <div className={style.carousel}>
                  <Carousel schema={answer} store="target" pagination="target" effect="resizing" />
                </div>
              </>
            ) : (
              <div className={style.images}>
                <div className={style.mainImage}>
                  {assetsCollection[0] ? (
                    <>
                      {ASSETS_TYPES.IMAGE === getAssetType(assetsCollection[0]) && (
                        <>
                          <button className={style.expandWrapper} onClick={() => onAssetModalOpen(0)}>
                            <div className={style.expand} />
                          </button>
                          <div className={style.heartWrapper}>
                            <EmptyLink color="#CC0000">
                              <div className={style.heart} />
                            </EmptyLink>
                          </div>
                          <ZoomableImage
                            asset={assetsCollection[0] as IImageAsset}
                            appearence="main"
                            answer={answer}
                            assetIndex={0}
                          />
                        </>
                      )}
                      {ASSETS_TYPES.MODEL === getAssetType(assetsCollection[0]) && (
                        <>
                          <button className={style.expandWrapper} onClick={() => onAssetModalOpen(0)}>
                            <div className={style.expand} />
                          </button>
                          <div className={style.heartWrapper}>
                            <EmptyLink color="#CC0000">
                              <div className={style.heart} />
                            </EmptyLink>
                          </div>
                          <div className={style.mainModel}>
                            <model-viewer
                              src={(assetsCollection[0] as IFileAsset).file_url}
                              camera-controls={false}
                              style={{ width: '100%', height: '100%' }}
                            />
                          </div>
                        </>
                      )}
                      {ASSETS_TYPES.VIDEO === getAssetType(assetsCollection[0]) && (
                        <button
                          className={style.mainVideo}
                          style={{
                            backgroundImage: `url(${(assetsCollection[0] as IEcommerceVideoAsset).video.thumbnail})`
                          }}
                          onClick={() => onAssetModalOpen(0)}
                        >
                          <div className={style.playIcon} />
                        </button>
                      )}
                    </>
                  ) : (
                    <div className={style.noImage} />
                  )}
                </div>
                {smallImageCollection.length > 4 ? (
                  <div className={style.collection}>
                    {smallImageCollection.map((asset, index) => (
                      <div className={style.smallImage} key={index}>
                        {index < 4 && renderSmallAsset(asset, index)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    {smallImageCollection.length > 0 && (
                      <div className={style.collection}>
                        {smallImageCollection.map((asset, index) => (
                          <div className={style.smallImage} key={index}>
                            {renderSmallAsset(asset, index)}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
                {showImages && (
                  <div className={style.collection}>
                    {smallImageCollection.map((asset, index) => (
                      <div className={style.smallImage} key={index}>
                        {index > 3 && renderSmallAsset(asset, index)}
                      </div>
                    ))}
                  </div>
                )}
                {smallImageCollection.length > 4 && (
                  <button className={style.showMore} onClick={() => setShowImages(!showImages)}>
                    {showImages
                      ? t('questions', 'ec', 'target', 'fewerImages')
                      : t('questions', 'ec', 'target', 'moreImages')}
                  </button>
                )}
              </div>
            )}
            <div className={style.content}>
              <EmptyLink color="#CC0000">
                <h6
                  dangerouslySetInnerHTML={{
                    __html: answer.sub_title || ''
                  }}
                />
              </EmptyLink>
              <p dangerouslySetInnerHTML={{ __html: answer.text }}></p>
              <div className={style.rating}>
                <Rating rating={answer.rating} size="big" />
                <EmptyLink color="#CC0000">
                  <div className={style.reviewsCount} dangerouslySetInnerHTML={{ __html: `${answer.reviews}` }} />
                </EmptyLink>
                <EmptyLink color="#CC0000">
                  <i className="pi pi-angle-down" />
                </EmptyLink>

                {answer.red_label && (
                  <div className={style.newAt}>
                    <span className={style.text}>&nbsp;| {answer.red_label}&nbsp;</span>
                    <span className={style.logo}>¬</span>
                  </div>
                )}
              </div>
              <div className={style.prices}>
                {isDiscounted && (
                  <>
                    <div className={style.discount}>
                      <span
                        className={style.nowPrice}
                        dangerouslySetInnerHTML={{
                          __html: `${formatPriceWithLocale(answer.discounted_price, schema.price_mask, languageTag, true, false)}`
                        }}
                      />
                      <span className={style.reg}>{t('questions', 'ec', 'target', 'was')}</span>
                      {answer.price > 0 && (
                        <span
                          className={`${style.lastPrice}`}
                          dangerouslySetInnerHTML={{
                            __html: `${formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)}`
                          }}
                        />
                      )}
                      {answer.per_count_price && <span className={style.perCount}>{answer.per_count_price}</span>}
                    </div>
                    <div className={style.saving}>
                      <span className={style.sale}>{t('questions', 'ec', 'target', 'newLowerPrice')}</span>
                      <span
                        className={style.save}
                        dangerouslySetInnerHTML={{
                          __html: `${t('questions', 'ec', 'target', 'save')} ${formatPriceWithLocale(
                            answer.price - answer.discounted_price,
                            schema.price_mask,
                            languageTag,
                            true,
                            false
                          )} ${t<(percentage: string) => string>(
                            'questions',
                            'ec',
                            'target',
                            'off'
                          )((((answer.price - answer.discounted_price) / answer.price) * 100).toFixed(1))}`
                        }}
                      ></span>
                    </div>
                  </>
                )}
                {!isDiscounted && answer.price && (
                  <>
                    <span
                      className={style.defaultPrice}
                      dangerouslySetInnerHTML={{
                        __html: formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)
                      }}
                    />
                    {answer.per_count_price && <span className={style.perCount}>{answer.per_count_price}</span>}
                  </>
                )}
              </div>
              {viewport.mobile && <div className={style.line} />}
              <Subproducts schema={schema} answer={answer} onSubitemSelect={onSubitemSelect} />
              <div className={style.shipping}>
                <span className={style.shipTo}>{t('questions', 'ec', 'target', 'shipTo')}</span>
                <EmptyLink color="#CC0000">
                  <span className={style.edit}>{t('questions', 'ec', 'target', 'edit')}</span>
                </EmptyLink>
              </div>
              <div className={style.delivery}>
                {t<(percentage: string) => string>('questions', 'ec', 'target', 'arrivesBy')(deliveryDate)}
              </div>
              <div className={style.shipsFree}>
                <span className={style.text}>{t('questions', 'ec', 'target', 'shipsFree')}</span>
                <span className={style.textUnderlined}>
                  <EmptyLink color="#CC0000">{t('questions', 'ec', 'target', 'exclusions')}</EmptyLink>
                </span>
              </div>
              <div className={style.buy}>
                {quantity > 0 && !findability ? (
                  <AddToCartButton
                    index={index}
                    value={quantity}
                    findability={findability}
                    width={viewport.mobile ? '100%' : '160px'}
                    size="big"
                    showDropdown={true}
                    limit={schema.max_qty_per_product}
                    onAddToCart={onAddToCart}
                  />
                ) : (
                  <div id={style.productDropdownContainer}>
                    {viewport.mobile ? (
                      <button
                        disabled={findability && 1 === quantity}
                        className={style.buyNow}
                        onClick={() => {
                          hapticEffects.selection()
                          onBuyNow(1, index)
                        }}
                      >
                        {t('questions', 'ec', 'target', 'buyNow')}
                      </button>
                    ) : (
                      <>
                        {!findability && (
                          <Dropdown
                            fixedDropdownDir="bottom"
                            value={`${dropdownValue}`}
                            options={createNumbersArray(schema.max_qty_per_product || 10, false)}
                            optionLabel="value"
                            valueTemplate={(option: { value: string }) => (
                              <div className={style.dropdownLabel}>
                                {t('questions', 'ec', 'target', 'qty')}&nbsp;<b>{`${option?.value}`}</b>
                              </div>
                            )}
                            onChange={event => {
                              hapticEffects.selection()
                              setDropdownValue(String(event.value))
                            }}
                          />
                        )}
                      </>
                    )}
                    <button
                      disabled={1 === quantity}
                      className={style.add}
                      onClick={() => {
                        hapticEffects.selection()
                        onAddToCart(Number(dropdownValue), index, true)
                      }}
                    >
                      {t('questions', 'ec', 'target', 'addToCart')}
                    </button>
                  </div>
                )}
              </div>
              <EmptyLink color="#CC0000">
                <div className={style.sold}>
                  <div className={style.icon} />

                  <div
                    className={style.soldText}
                    dangerouslySetInnerHTML={{ __html: `${t('questions', 'ec', 'target', 'shipAndSold')}` }}
                  />
                  <i className="pi pi-chevron-right" />
                </div>
              </EmptyLink>
              <div className={style.report}>
                <EmptyLink color="#CC0000">{t('questions', 'ec', 'target', 'report')}</EmptyLink>
              </div>
              {viewport.mobile && (
                <>
                  <div className={style.line} />
                  <div className={style.ad}>
                    <div className={style.cardIcon} />
                    <div className={style.textContent}>
                      <div className={style.title}>{t('questions', 'ec', 'target', 'save5')}</div>
                      <div className={style.subtitle}>{t('questions', 'ec', 'target', 'save5with')}</div>
                    </div>
                    <EmptyLink color="#CC0000">
                      <i className="pi pi-info-circle" />
                    </EmptyLink>
                  </div>
                  <div className={style.adLine} />
                  <div className={style.ad}>
                    <div className={style.affirmIcon} />
                    <div className={style.textContent}>
                      <div className={style.title}>{t('questions', 'ec', 'target', 'asLow')}</div>
                      <div className={style.subtitle}>{t('questions', 'ec', 'target', 'affirm')}</div>
                    </div>
                    <EmptyLink color="#CC0000">
                      <i className="pi pi-info-circle" />
                    </EmptyLink>
                  </div>
                  <div className={style.line} />
                </>
              )}
              <div className={style.wish}>
                <div className={style.title}>
                  <div className={style.icon} />
                  <p>{t('questions', 'ec', 'target', 'eligible')}</p>
                </div>
                <EmptyLink color="#CC0000">
                  <div className={style.button}>
                    <i className="pi pi-plus" /> {t('questions', 'ec', 'target', 'addItem')}
                  </div>
                </EmptyLink>
              </div>
            </div>
          </AssetsGate>
        </div>
        <div className={style.description}>
          <h6>{t('questions', 'ec', 'target', 'aboutItem')}</h6>
          <div className={style.tabs}>
            <div className={style.tabHeader}>
              <div className={style.head}>{t('questions', 'ec', 'target', 'details')}</div>
              <EmptyLink color="#CC0000">
                <i
                  className={answer.description && answer.feature_bullets ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                />
              </EmptyLink>
            </div>
            <div className={style.descriptionWrapper}>
              {answer.description && (
                <div className={style.block}>
                  <div className={style.subTitle}>{t('questions', 'ec', 'target', 'description')}</div>
                  <div
                    className={style.descriptionText}
                    dangerouslySetInnerHTML={{
                      __html: unescapeTags(answer.description) as string
                    }}
                  />
                </div>
              )}
              {answer.feature_bullets && (
                <div className={style.block}>
                  <div className={style.subTitle}>{t('questions', 'ec', 'target', 'features')}</div>
                  <div
                    className={style.descriptionText}
                    dangerouslySetInnerHTML={{
                      __html: unescapeTags(answer.feature_bullets) as string
                    }}
                  />
                </div>
              )}
            </div>
            <div className={`${style.tabHeader} ${style.tabHeaderBottom} ${style.tabHeaderTop}`}>
              <div className={style.head}>{t('questions', 'ec', 'target', 'specifications')}</div>
              <EmptyLink color="#CC0000">
                <i className="pi pi-chevron-down" />
              </EmptyLink>
            </div>
            <div className={`${style.tabHeader} ${style.tabHeaderBottom}`}>
              <div className={style.head}>{t('questions', 'ec', 'target', 'shipAndReturns')}</div>
              <EmptyLink color="#CC0000">
                <i className="pi pi-chevron-down" />
              </EmptyLink>
            </div>
            {viewport.mobile && (
              <>
                <div className={style.line} />
                <EmptyLink color="#CC0000">
                  <div className={style.reviews}>
                    <div className={style.textContent}>
                      <div className={style.title}>{t('questions', 'ec', 'target', 'reviews')}</div>
                      <div className={style.ratingInfo}>
                        <Rating rating={answer.rating} size="big" />
                        <div
                          className={style.ratingText}
                          dangerouslySetInnerHTML={{
                            __html: `${t<(rating: number, reviews: number) => string>(
                              'questions',
                              'ec',
                              'target',
                              'rating'
                            )(answer.rating, answer.reviews)}`
                          }}
                        />
                      </div>
                    </div>
                    <i className="pi pi-chevron-right" />
                  </div>
                </EmptyLink>
                <div className={style.line} />
              </>
            )}
            <div className={`${style.tabHeader} ${style.tabHeaderBottom}`}>
              <div className={style.head}>{t('questions', 'ec', 'target', 'qa')}</div>
              <EmptyLink color="#CC0000">
                <i className="pi pi-chevron-down" />
              </EmptyLink>
            </div>
            {viewport.mobile && (
              <div className={`${style.tabHeader} ${style.tabHeaderBottom}`}>
                <div className={style.head}>{t('questions', 'ec', 'target', 'policies')}</div>
                <EmptyLink color="#CC0000">
                  <i className="pi pi-chevron-down" />
                </EmptyLink>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default Product
